/**
 * Phase WP3.2 · KycVerificationPanel (Feb 2026)
 *
 * Inline Settings tab that surfaces the Wyzia KYC step required to
 * complete onboarding. Backend `_compute_seller_setup_status` now
 * includes a 6th `kyc_wyzia` step that flips `complete: true` once
 * `users.wyzia_kyc.status === 'SUCCESS'` — without it, the setup
 * checklist "Complete setup" CTA stays disabled.
 *
 * UX:
 *   1. If no KYC has been started (or Middesk hasn't completed yet),
 *      shows a "Start verification" CTA → POST /onboarding/kyc/start
 *      → opens the Wyzia-hosted kyc_url in a new tab + polls every 5s.
 *   2. Once Wyzia returns status=SUCCESS, flips the chip green and tells
 *      the user the step is complete + bumps `useSellerSetup().refresh()`.
 *   3. FAILED / EXPIRED → re-start button.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { Loader2, ShieldCheck, ExternalLink, AlertCircle, Check, RotateCw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { useSellerSetup } from "../context/SellerSetupContext";
import VerificationFrame from "./VerificationFrame";


const TERMINAL_OK   = new Set(["SUCCESS", "COMPLETED"]);
const TERMINAL_FAIL = new Set(["FAILED", "EXPIRED"]);


export default function KycVerificationPanel() {
  const setup = useSellerSetup();
  const [state, setState] = useState(null);   // null = unknown; {} = not started
  const [busy,  setBusy]  = useState(false);
  const [err,   setErr]   = useState(null);
  const [frameOpen, setFrameOpen] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/onboarding/kyc/status");
      setState(r.data || {});
      setErr(null);
      if (TERMINAL_OK.has(r.data?.status)) {
        // Tell the parent checklist to re-poll so the new step flips green.
        setup?.refresh?.();
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      }
    } catch (e) {
      setErr(e.response?.data?.detail?.message || e.message);
    }
  }, [setup]);

  useEffect(() => {
    load();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [load]);

  // While a request is INITED/PROCESSING, poll every 5s.
  useEffect(() => {
    const s = state?.status;
    if (state?.started && s && !TERMINAL_OK.has(s) && !TERMINAL_FAIL.has(s)) {
      pollRef.current = setInterval(load, 5000);
      return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }
  }, [state?.status, state?.started, load]);

  const startKyc = async () => {
    // Verification runs inside Verqo — never a redirect to the partner's site.
    setFrameOpen(true);
  };

  const reopenKyc = () => setFrameOpen(true);

  // ── Render ──
  if (state === null) {
    return (
      <div className="p-8 flex items-center gap-3 text-foreground/55 text-[13px]">
        <Loader2 className="w-4 h-4 animate-spin"/>Loading verification status…
      </div>
    );
  }

  const status   = state.status;
  const isDone   = TERMINAL_OK.has(status);
  const isFailed = TERMINAL_FAIL.has(status);
  const isPending = state.started && !isDone && !isFailed;

  return (
    <div className="max-w-2xl space-y-5" data-testid="kyc-verification-panel">
      <div>
        <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Verification</div>
        <h2 className="display text-xl tracking-tight font-semibold mt-1">Identity & bank verification</h2>
        <p className="text-[13px] text-foreground/65 mt-2 leading-relaxed">
          To send and receive money on Verqo, we verify your business identity and link
          your operating bank. This protects your buyers and unlocks instant payouts.
It takes about three minutes.
        </p>
      </div>

      {/* Status card */}
      <div className={`rounded-2xl border-2 p-5 ${
        isDone
          ? "border-emerald-300 bg-emerald-50/60"
          : isFailed
          ? "border-rose-300 bg-rose-50/60"
          : isPending
          ? "border-amber-300 bg-amber-50/60"
          : "border-border bg-secondary/30"
      }`}>
        <div className="flex items-start gap-3">
          <div className={`shrink-0 w-10 h-10 rounded-xl grid place-items-center ${
            isDone ? "bg-emerald-600 text-white" :
            isFailed ? "bg-rose-600 text-white" :
            isPending ? "bg-amber-500 text-white" :
            "bg-foreground text-background"
          }`}>
            {isDone ? <Check className="w-5 h-5"/> :
             isFailed ? <AlertCircle className="w-5 h-5"/> :
             isPending ? <Loader2 className="w-5 h-5 animate-spin"/> :
             <ShieldCheck className="w-5 h-5"/>}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[14px] font-semibold flex items-center gap-2">
              {isDone     && "Verification complete"}
              {isFailed   && "Verification failed"}
              {isPending  && "Verification in progress"}
              {!state.started && "Verification not started yet"}
              {status && (
                <span data-testid="kyc-status-chip"
                      className="text-[10px] mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-foreground/10 text-foreground/70 normal-case">
                  {status}
                </span>
              )}
            </div>
            <div className="text-[12.5px] text-foreground/65 mt-1 leading-snug">
              {isDone    && `You verified your identity and bank on ${new Date(state.completed_at || state.updated_at || Date.now()).toLocaleDateString()}. You can now move money on Verqo.`}
              {isFailed  && "The verification flow didn't complete. Click below to retry — you'll start fresh."}
              {isPending && "We are checking your details. This updates by itself — you can carry on using Verqo."}
              {!state.started && "Start verification and finish the short form right here in Verqo."}
            </div>
            {state.is_mock && (
              <div className="text-[10.5px] mono uppercase tracking-wider text-foreground/50 mt-2 inline-flex items-center gap-1">
                <AlertCircle className="w-2.5 h-2.5"/>
                mock fallback · sandbox unavailable
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {!state.started && (
            <Button
              onClick={startKyc}
              disabled={busy}
              data-testid="kyc-start-btn"
              className="rounded-full bg-foreground text-background"
            >
              {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5"/>Starting…</> :
                      <>Start verification</>}
            </Button>
          )}
          {isPending && (
            <>
              <Button
                onClick={reopenKyc}
                data-testid="kyc-reopen-btn"
                className="rounded-full bg-foreground text-background"
              >
                Re-open verification tab <ExternalLink className="w-3.5 h-3.5 ml-1.5"/>
              </Button>
              <Button
                variant="ghost"
                onClick={load}
                data-testid="kyc-refresh-btn"
                className="rounded-full"
              >
                <RotateCw className="w-3.5 h-3.5 mr-1.5"/>Refresh status
              </Button>
            </>
          )}
          {isFailed && (
            <Button
              onClick={startKyc}
              disabled={busy}
              data-testid="kyc-retry-btn"
              className="rounded-full bg-foreground text-background"
            >
              {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5"/>Starting…</> : "Restart verification"}
            </Button>
          )}
        </div>
      </div>

      {/* Meta */}
      {state.request_id && (
        <div className="rounded-xl border border-border bg-white p-4 text-[12px] text-foreground/65 space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-foreground/50">Request ID</span>
            <span className="mono">{state.request_id}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-foreground/50">Started</span>
            <span>{state.started_at ? new Date(state.started_at).toLocaleString() : "—"}</span>
          </div>
          {state.trigger && (
            <div className="flex items-center justify-between">
              <span className="text-foreground/50">Triggered by</span>
              <span className="text-[11px]">{state.trigger}</span>
            </div>
          )}
        </div>
      )}

      {err && (
        <div className="text-[12px] text-rose-700 flex items-center gap-1.5">
          <AlertCircle className="w-3.5 h-3.5"/>{err}
        </div>
      )}

      <VerificationFrame open={frameOpen} onOpenChange={setFrameOpen} onApproved={load} />
    </div>
  );
}
